import { useForm } from '@mantine/form'
import {
  EntityParams
} from 'app/models/entity-params'
import { useState } from 'react'
import { loadEntities, saveEntity } from 'app/state/ducks/entities/thunks'
import { useDispatch, useSelector } from 'react-redux'
import config from 'app/config/config'
import { formatDateToUTC, isAlphanumeric, isNumeric, stringToDate } from 'app/utils/util-functions'
import { useNavigate } from 'react-router-dom'
import {
  ErrorNotification,
  SuccessNotification,
} from 'app/views/components/notifications/notification'
import { getEntityProfiles } from 'app/state/ducks/entity-profile/selectors'
import { getEntityTypes } from 'app/state/ducks/entity-type/selectors'
import { EntityTypeHierarchy } from 'app/models/entity-type-params'
import { EntityRole, ThirdPartyRoles } from 'app/models/entity-profile-params'
import EntitiesFormPresenation from './entities-form-presentation'
import EntitiesFormThirdPartyProviders from './entities-form-third-party-providers'
import EntitiesFormSignatureBlock from './entities-form-signature-block'
import EntitiesFormDeliverySettings from './entities-form-delivery-settings'
import { getEntities } from 'app/state/ducks/entities/selectors'
import { getCompanies } from 'app/state/ducks/companies/selectors'

type EntitiesFormProps = {
  entity?: EntityParams
  closeModal: () => void
  isStepper?: boolean
  onHandleSubmit?: any
  withRedirection?: boolean
  creationType?: string
}

const GIN_REGEX = /^[A-NP-Z0-9]{6}\.[A-NP-Z0-9]{5}\.(?:LE|SL|ME|BR|SP|SF|SD|SS|SB)\.\d{3}$/
const EIN_REGEX = /^\d{2}-\d{7}$/

// Validates EIN format: XX-XXXXXXX (2 digits, dash, 7 digits)
const isValidEIN = (ein: string): boolean => {
  return EIN_REGEX.test(ein)
}

export default function EntitiesForm({
  entity,
  closeModal,
  isStepper,
  onHandleSubmit,
  withRedirection,
  creationType,
}: EntitiesFormProps) {
  const [isLoading, setIsLoading] = useState(false)
  const entityTypes = useSelector(getEntityTypes)
  const entityProfiles = useSelector(getEntityProfiles)
  const entities = useSelector(getEntities)
  const companies = useSelector(getCompanies)

  const navigate = useNavigate()

  const dispatch = useDispatch()

  type FormValues = Record<string, any>;


  const transformValues = (values: FormValues) => ({
    ...values,
    ukTreatyPassportExpiryDate: values.ukTreatyPassportExpiryDate
      ? formatDateToUTC(values.ukTreatyPassportExpiryDate)
      : null,
    kycType: values.kycType || null,
    fundManagerMei:
      entityTypes
        .find((type) => type.id === values.entityType)
        ?.hierarchy &&
        ["HierarchyFundManager", "HierarchyBoth"].includes(
          entityTypes.find((type) => type.id === values.entityType)?.hierarchy || ""
        )
        ? values.fundManagerMei
        : null,
    fundManager:
      entityTypes
        .find((type) => type.id === values.entityType)
        ?.hierarchy &&
        ["HierarchyFundManager", "HierarchyBoth"].includes(
          entityTypes.find((type) => type.id === values.entityType)?.hierarchy || ""
        )
        ? values.fundManager
        : null,
    parentMei:
      entityTypes
        .find((type) => type.id === values.entityType)
        ?.hierarchy &&
        ["HierarchyParent", "HierarchyBoth"].includes(
          entityTypes.find((type) => type.id === values.entityType)?.hierarchy || ""
        )
        ? values.parentMei || null
        : null,
    parent:
      entityTypes
        .find((type) => type.id === values.entityType)
        ?.hierarchy &&
        ["HierarchyParent", "HierarchyBoth"].includes(
          entityTypes.find((type) => type.id === values.entityType)?.hierarchy || ""
        )
        ? values.parent
        : null,
    institutionType: values.institutionType || null,
    ukTreatyPassportNumber: values.ukTreatyPassportNumber || null,
    entityType: values.entityType
      ? {
        id: entityTypes.find((type) => type.id === values.entityType)?.id || "",
        admin: entityTypes.find((type) => type.id === values.entityType)?.agencyAdmin || "",
      }
      : null,
    entityProfile: values.entityProfile.map((profileKey: string) => {
      const profile = entityProfiles.find((profile) => profile.id === profileKey);
      return { id: profile?.id || "", admin: profile?.agencyAdmin || "" };
    }),
    legalEntityId: values.legalEntityId && values.legalEntityId != '' ? values.legalEntityId : null,
    mei: values.mei && values.mei != '' ? values.mei : null,
    ein: values.ein && values.ein != '' ? values.ein : null,
    cei: values.cei && values.cei != '' ? values.cei : null,
    agentEntityId: values.agentEntityId && values.agentEntityId != '' ? values.agentEntityId : null,
    naicCode: values.naicCode && values.naicCode != '' ? values.naicCode : null,
    giin: values.giin && values.giin != '' ? values.giin : null,
    crn: values.crn && values.crn != '' ? values.crn : null,
    taxFormExpirationDate:
      formatDateToUTC(values.taxFormExpirationDate) === 'Invalid date'
        ? null
        : formatDateToUTC(values.taxFormExpirationDate),
    thirdPartyProviders: (() => {
      const providers = [...(values.thirdPartyProviders ?? [])];
      const lastProvider = providers[providers.length - 1];

      if (
        lastProvider &&
        (!lastProvider.thirdPartyRole?.trim() && !lastProvider.thirdPartyProfile?.id?.trim())
      ) {
        providers.pop(); // Remove last empty provider
      }

      return providers.map((provider) => {
        const selectedEntityId = provider.thirdPartyProfile?.id;

        const matchingEntity = entities.find((entity) => entity.id === selectedEntityId);

        if (!matchingEntity) {
          throw new Error(`Could not find entity for entity ID: ${selectedEntityId}`);
        }

        return {
          thirdPartyRole: provider.thirdPartyRole,
          thirdPartyProfile: {
            id: matchingEntity.id || '',
            admin: matchingEntity.entityType?.admin || '',
          },
          deliverDocs: provider.deliverDocs,
        };
      });
    })(),
    signatureBlock: Object.values(values.signatureBlock ?? {}).some(Boolean)
      ? values.signatureBlock
      : null,
    deliverySettings: (() => {
      const settings = [...(values.deliverySettings ?? [])];
      const lastSetting = settings[settings.length - 1];

      if (
        lastSetting &&
        (!lastSetting.deliveryRole?.trim() && !lastSetting.deliveryMethod?.trim() && !lastSetting.deliveryEmails?.trim())
      ) {
        settings.pop(); // Remove last empty setting
      }

      return settings.map((setting) => ({
        deliveryRole: setting.deliveryRole,
        deliveryMethod: setting.deliveryMethod,
        deliveryEmails: setting.deliveryEmails
          ? setting.deliveryEmails.split(';').map((email: string) => email.trim()).filter((email: string) => email)
          : []
      }));
    })(),
    hasError: false,
    hasSuccessfulEntitySave: false,
    ErrorMessage: "",
    customer_token: config.company.toUpperCase(),
    companyId: values.companyId
      ? {
        id: companies.find((type) => type.prefix === values.companyId)?.prefix || "",
        admin: companies.find((type) => type.prefix === values.companyId)?.lendosAdmin || "",
      }
      : null,
  });

  // Mapping of Mandatory field name to the form field name
  const mandatoryFieldMapping = {
    EntityName: 'entityName',
    MEI: 'mei',
    InstitutionType: 'institutionType',
    KycType: 'kycType',
    Address: 'address',
    AddressLine2: 'address2',
    City: 'city',
    State: 'state',
    PostalCode: 'zipCode',
    Parent: 'parent',
    ParentMEI: 'parentMei',
    FundManagerName: 'fundManager',
    FundManagerMEI: 'fundManagerMei',
    LEI: 'legalEntityId',
    CEI: 'cei',
    NAICCode: 'naicCode',
    CRN: 'crn',
    EIN: 'ein',
    GIIN: 'giin',
    InternalId: 'internalId',
    EntityReference: 'entityReference',
    UsTaxFormType: 'usTaxFormType',
    TaxFormStatus: 'taxFormStatus',
    KycStatus: 'kycStatus',
    CountryOfIncorporation: 'countryOfIncorporation',
    CountryOfTaxResidence: 'countryOfTaxResidence',
    CountryRegion: 'country',
  };

  const form = useForm({
    initialValues: {
      id: entity?.id ?? '',
      entityName: entity?.entityName ?? '',
      entityType: entity?.entityType?.id ?? null,
      entityProfile: entity?.entityProfile.map(profileKey => profileKey.id) ?? [],
      entityReference: entity?.entityReference ?? null,
      address: entity?.address ?? null,
      address2: entity?.address2 ?? null,
      city: entity?.city ?? null,
      state: entity?.state ?? null,
      zipCode: entity?.zipCode ?? null,
      country: entity?.country ?? null,
      kycType: entity?.kycType ?? '',
      kycStatus: entity?.kycStatus ?? '',
      systemId: entity?.systemId ?? null,
      institutionType: entity?.institutionType ?? '',
      countryOfIncorporation: entity?.countryOfIncorporation ?? null,
      countryOfTaxResidence: entity?.countryOfTaxResidence ?? null,
      taxFormStatus: entity?.taxFormStatus ?? null,
      taxFormExpirationDate: entity?.taxFormExpirationDate ?? null,
      usTaxFormType: entity?.usTaxFormType ?? null,
      fundManager: entity?.fundManager ?? null,
      fundManagerMei: entity?.fundManagerMei ?? null,
      parent: entity?.parent ?? null,
      parentMei: entity?.parentMei ?? null,
      giin: entity?.giin ?? null,
      crn: entity?.crn ?? null,
      naicCode: entity?.naicCode ?? null,
      internalId: entity?.internalId ?? null,
      legalEntityId: entity?.legalEntityId ?? null,
      mei: entity?.mei ?? null,
      ein: entity?.ein ?? null,
      cei: entity?.cei ?? null,
      agentEntityId: entity?.agentEntityId ?? null,
      ukTreatyPassportNumber: entity?.ukTreatyPassportNumber ?? null,
      status: entity?.status ?? '',
      ukTreatyPassportExpiryDate: entity?.ukTreatyPassportExpiryDate
        ? stringToDate(entity?.ukTreatyPassportExpiryDate)
        : null,
      entityParty: entity?.entityParty ?? null,
      thirdPartyProviders: (entity?.thirdPartyProviders ?? []).map((provider: any) => ({
        ...provider,
        deliverDocs: provider.deliverDocs ?? false, // Ensure deliverDocs has a default value
      })),
      deliverySettings: entity?.deliverySettings?.map((setting: any) => ({
        deliveryRole: setting.deliveryRole ?? '',
        deliveryMethod: setting.deliveryMethod ?? '',
        deliveryEmails: Array.isArray(setting.deliveryEmails)
          ? setting.deliveryEmails.join('; ')
          : setting.deliveryEmails ?? '',
      })) ?? [],
      signatureBlock: {
        signatureText: entity?.signatureBlock?.signatureText ?? null,
        signatureTitle: entity?.signatureBlock?.signatureTitle ?? null,
        signatureName: entity?.signatureBlock?.signatureName ?? null,
        signatureEmail: entity?.signatureBlock?.signatureEmail ?? null,
      },
      companyId: entity?.companyId?.id ?? null,
      hasError: entity?.hasError ?? false,
      ErrorMessage: entity?.ErrorMessage ?? '',
      hasSuccessfulEntitySave: entity?.hasSuccessfulEntitySave ?? false,
      customer_token: entity?.customer_token ?? config.company.toUpperCase(),
    },
    validate: (values) => {
      const errors: Record<string, any> = {};
      // Required fields for draft entity
      if (!values.entityName?.trim()) errors.entityName = 'Required';
      if (!values.kycStatus?.trim()) errors.kycStatus = 'Required';

      // Dynamically required fields based on entityType
      const selectedEntityType = entityTypes.find(type => type.id === values.entityType);
      if (selectedEntityType) {
        const mandatoryFields = selectedEntityType.mandatoryFields || [];
        mandatoryFields.forEach((field) => {
          const formattedField = mandatoryFieldMapping[field];
          if (!values[formattedField as keyof typeof values]?.toString().trim()) {
            errors[formattedField] = 'Required';
          }
        });
      }

      // Ensure Country is selected if State is entered
      if (!values.country && values.state) {
        errors.country = "A country must be selected before choosing a state.";
      }

      // Fields not required for draft entity, but validate if data is entered
      if (values.fundManagerMei) {
        if (
          [EntityTypeHierarchy.HierarchyFundManager, EntityTypeHierarchy.HierarchyBoth].includes(
            selectedEntityType?.hierarchy as EntityTypeHierarchy
          ) && values.fundManagerMei.length !== 10
        ) {
          errors.fundManagerMei = '10 characters';
        }
      }

      if (values.parentMei) {
        if (
          [EntityTypeHierarchy.HierarchyParent, EntityTypeHierarchy.HierarchyBoth].includes(
            selectedEntityType?.hierarchy as EntityTypeHierarchy
          ) && values.parentMei.length !== 10
        ) {
          errors.parentMei = '10 characters';
        }
      }

      if (values.legalEntityId && values.legalEntityId.length !== 20) {
        errors.legalEntityId = '20 characters';
      }

      if (values.giin && !GIN_REGEX.test(values.giin)) {
        errors.giin = 'GIIN must be in XXXXXX.XXXXX.XX.XXX format. (example: "AZ9123.AXCV0.LE.123")';
      }

      if (values.crn && (values.crn.length !== 8 || !isAlphanumeric(values.crn))) {
        errors.crn = '8 Alphanumeric Characters';
      }

      if (values.ein && !isValidEIN(values.ein)) {
        errors.ein = 'EIN must be in XX-XXXXXXX format (e.g., 12-3456789)';
      }

      if (values.cei && values.cei.length !== 10) {
        errors.cei = '10 characters';
      }

      if (values.naicCode && (values.naicCode.length < 2 || values.naicCode.length > 6 || !isNumeric(values.naicCode))) {
        errors.naicCode = 'Between 2 and 6 numeric characters';
      }

      if (values.mei && values.mei.length !== 10) {
        errors.mei = 'MEI Should Be 10 Characters';
      }

      if (values.zipCode && !isAlphanumeric(values.zipCode)) {
        errors.zipCode = 'ZipCode must be Alphanumeric';
      }


      type ProviderErrors = { thirdPartyRole?: string; thirdPartyProfile?: string };
      const thirdPartyErrors: (ProviderErrors | undefined)[] = [];

      const cleanedProviders = values.thirdPartyProviders?.filter(
        (p) => p.thirdPartyRole?.trim() || p.thirdPartyProfile?.id?.trim()
      ) || [];

      cleanedProviders.forEach((provider, index) => {
        const providerErrors: Record<string, string> = {};

        if (provider.thirdPartyRole) {
          const matchingProfiles = entityProfiles?.filter(profile =>
            profile.roles.includes(provider.thirdPartyRole)
          );

          if (!matchingProfiles || matchingProfiles.length === 0) {
            providerErrors.thirdPartyProfile = 'No Profiles Found';
          } else if (provider.thirdPartyProfile?.id?.trim()) {
            // NEW: Find entity first
            const entity = entities.find(entity =>
              entity.id === provider.thirdPartyProfile.id
            );

            if (!entity) {
              providerErrors.thirdPartyProfile = 'No Entity Found for this Profile';
            } else {
              // Find if any profile in the entity matches the selected role
              const hasMatchingProfile = entity.entityProfile.some(profileStableKey =>
                entityProfiles?.some(p => p.id === profileStableKey.id && p.roles.includes(provider.thirdPartyRole))
              );

              if (!hasMatchingProfile) {
                providerErrors.thirdPartyProfile = 'No Profile in Entity Matches Selected Role';
              }
            }
          }
        }

        if (Object.keys(providerErrors).length > 0) {
          thirdPartyErrors[index] = providerErrors;
        }
      });

      if (thirdPartyErrors.length > 0) {
        errors.thirdPartyProviders = thirdPartyErrors;
      }

      const hasIncompleteRow = cleanedProviders.some(
        (provider) =>
          !provider.thirdPartyRole?.trim() ||
          !provider.thirdPartyProfile?.id?.trim()
      );

      if (hasIncompleteRow) {
        errors._form = 'Each provider must have a role and a profile';
      }

      // Validate deliverySettings
      type SettingErrors = { deliveryRole?: string; deliveryMethod?: string; deliveryEmails?: string };
      const deliverySettingsErrors: (SettingErrors | undefined)[] = [];

      const cleanedSettings = values.deliverySettings?.filter(
        (s: any) => s.deliveryRole?.trim() || s.deliveryMethod?.trim() || s.deliveryEmails?.trim()
      ) || [];

      cleanedSettings.forEach((setting: any, index: number) => {
        const settingErrors: Record<string, string> = {};

        // Check if entity has any profiles that match the selected role.
        // The Agent delivery role is granted by the Admin Agent profile role
        // (ADR_LOS-026) — the two enums name it differently.
        if (setting.deliveryRole) {
          const requiredProfileRole =
            setting.deliveryRole === ThirdPartyRoles.AgentRole
              ? EntityRole.AdminAgentRole
              : setting.deliveryRole;
          const hasMatchingProfile = entityProfiles?.some(profile =>
            profile.id && values.entityProfile.includes(profile.id) &&
            profile.roles.includes(requiredProfileRole)
          );

          if (!hasMatchingProfile) {
            settingErrors.deliveryRole = 'No matching profile found in entity';
          }
        }

        if (Object.keys(settingErrors).length > 0) {
          deliverySettingsErrors[index] = settingErrors;
        }
      });

      if (deliverySettingsErrors.length > 0) {
        errors.deliverySettings = deliverySettingsErrors;
      }

      const hasIncompleteSettingRow = cleanedSettings.some(
        (setting: any) =>
          !setting.deliveryRole?.trim() ||
          !setting.deliveryMethod?.trim() ||
          !setting.deliveryEmails?.trim()
      );

      if (hasIncompleteSettingRow) {
        errors._form = 'Each delivery setting must have a profile, method, and email addresses';
      }


      return errors;

    },
    transformValues: (values) => transformValues(values),
    validateInputOnBlur: true,
  })

  async function onSubmit(entityParams: any) {
    try {
      setIsLoading(true)
      const response: any = await dispatch(saveEntity(entityParams, entity))

      if (response.success) {
        SuccessNotification({
          title: 'Successful Save Entity',
          message: 'You created or edited an entity',
        })
        if (withRedirection) {
          closeModal()
          navigate(
            `/entitymanagement/entity/${response?.payload?.data?.id}/Inactive`
          )
        }
        return response.payload
      } else {
        ErrorNotification({
          title: 'Entity save failed',
          message: response.payload ?? 'Check data and try again',
        })
      }
      return undefined
    } catch {
      ErrorNotification({
        title: 'Entity save failed',
        message: 'Check data and try again',
      })
    } finally {
      setIsLoading(false)
    }
  }

  async function submitForm(event: any) {
    event.preventDefault()
    if (!form.validate().hasErrors) {
      const entity = await onSubmit(form.getTransformedValues())
      onHandleSubmit(entity ?? form.values)
    } else {
      ErrorNotification({
        title: 'Form Validation Error',
        message: 'Please fill in required fields',
      });
    }
  }

  async function submitAndExit() {
    if (!form.validate().hasErrors) {
      withRedirection = true
      const entity = await onSubmit(form.getTransformedValues())
      onHandleSubmit(entity ?? form.values)
    } else {
      ErrorNotification({
        title: 'Form Validation Error',
        message: 'Please fill in required fields',
      });
    }
  }

  async function handleBulkNotificationAndClosure() {
    SuccessNotification({
      title: 'Successfully Saved Bulk Entities',
      message: 'You created many entities',
    })
    dispatch(loadEntities())
    closeModal();
  }

  // pass mandatory fields (based on the selected entity type) down to the form to asterisk required fields
  const selectedEntityType = entityTypes.find(type => type.id === form.values.entityType);
  const mandatoryFields = selectedEntityType?.mandatoryFields?.map(field => mandatoryFieldMapping[field]) || [];

  const formReturn = () => {
    if (creationType && creationType === 'thirdPartyProviders') {
      return (
        <EntitiesFormThirdPartyProviders
          transformValues={transformValues}
          handleBulkNotificationAndClosure={handleBulkNotificationAndClosure}
          form={form}
          isLoading={isLoading}
          isStepper={isStepper}
          mandatoryFields={mandatoryFields}
          submitForm={submitForm}
          submitAndExit={submitAndExit}
          entityProfiles={entityProfiles}
        />
      )
    } else if (creationType && creationType === 'signatureBlock') {
      return (
        <EntitiesFormSignatureBlock
          transformValues={transformValues}
          handleBulkNotificationAndClosure={handleBulkNotificationAndClosure}
          form={form}
          isLoading={isLoading}
          isStepper={isStepper}
          mandatoryFields={mandatoryFields}
          submitForm={submitForm}
          submitAndExit={submitAndExit}
          exitForm={closeModal}
        />
      )
    } else if (creationType && creationType === 'deliverySettings') {
      return (
        <EntitiesFormDeliverySettings
          form={form}
          isLoading={isLoading}
          isStepper={isStepper}
          submitForm={submitForm}
          submitAndExit={submitAndExit}
          entityProfiles={entityProfiles}
        />
      )
    } else {
      return (
        <EntitiesFormPresenation
          transformValues={transformValues}
          handleBulkNotificationAndClosure={handleBulkNotificationAndClosure}
          form={form}
          isLoading={isLoading}
          isStepper={isStepper}
          mandatoryFields={mandatoryFields}
          submitForm={submitForm}
          submitAndExit={submitAndExit}
        />
      )
    }
  }

  return (
    formReturn()
  )
}


